import { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
   matchPath,
   Outlet,
   useLocation,
   useNavigate,
   useParams,
} from "react-router"
import Controls from "./Controls"
import Loader from "@/components/Loader"
import LogPanel from "@/components/LogPanel"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { SpinnerSize } from "@/components/shared/Spinner"
import Tabs from "@/components/shared/Tabs"
import { CellDetailOverviewRoutes } from "@/constants/routes"
import { useOpenCellSchedulingEnabled } from "@/hooks/useOpenCellSchedulingEnabled"
import { useUserRole } from "@/hooks/useUserRole"
import { AppDispatch, RootState } from "@/store"
import { selectTimezone } from "@/store/facilitiesSlice"
import {
   fetchFacilityLevel,
   updateFacilityLevel,
} from "@/store/facilityLevelsSlice"
import { fetchCellLogs } from "@/store/logsSlice"
import { LevelForm } from "@/types/facility"
import { Feature } from "@/types/user"
import { getSensor } from "@/utils/sensors"
import "./index.scss"

export interface CellDetailOverviewForm {
   maintenance: boolean
   vacant: boolean
}

const CellDetailOverview = () => {
   const dispatch = useDispatch<AppDispatch>()
   const navigate = useNavigate()
   const location = useLocation()
   const { id, alertId } = useParams<{ id: string; alertId: string }>()
   const { facilityLevel, loadingFacilityLevel } = useSelector(
      ({ facilityLevelsReducer }: RootState) => facilityLevelsReducer
   )
   const { cellLogs, loadingCellLogs, errorCellLogs } = useSelector(
      ({ logsReducer }: RootState) => logsReducer
   )
   const canAccessExport = useUserRole(Feature.EXPORT)
   const isOpenCellSchedulingEnabled = useOpenCellSchedulingEnabled()
   const { name, alertConfigurations, maintenance, vacant, sensors } =
      facilityLevel
   const [isCellLogExpanded, setIsCellLogExpanded] = useState(false)

   useEffect(() => {
      async function fetchLevel() {
         if (!id) return
         try {
            await dispatch(fetchFacilityLevel(id)).unwrap()
         } catch (error) {
            console.error("Error fetching facility level:", error)
         }
      }
      fetchLevel()
   }, [dispatch, id])

   const tabItems = useMemo(
      () => [
         {
            id: "summary",
            label: "Summary",
            value: "summary",
            route: CellDetailOverviewRoutes.SUMMARY,
         },
         {
            id: "alert-settings",
            label: "Alarm Settings",
            value: "alert-settings",
            route: CellDetailOverviewRoutes.ALERT_SETTINGS,
         },
         ...(canAccessExport
            ? [
                 {
                    id: "export",
                    label: "Export",
                    value: "export",
                    route: CellDetailOverviewRoutes.EXPORT,
                 },
              ]
            : []),
         ...(isOpenCellSchedulingEnabled
            ? [
                 {
                    id: "open-cell-schedules",
                    label: "Open Cell Schedules",
                    value: "open-cell-schedules",
                    route: CellDetailOverviewRoutes.OPEN_CELL_SCHEDULES,
                 },
              ]
            : []),
      ],
      [canAccessExport, isOpenCellSchedulingEnabled]
   )

   const cellSensor = useMemo(() => getSensor(sensors), [sensors])

   const activeTabId = useMemo(() => {
      const currentRoute = location.pathname
      const activeTab = tabItems.find((tab) =>
         matchPath({ path: tab.route, end: false }, currentRoute)
      )
      return activeTab ? activeTab.id : tabItems[0].id
   }, [location.pathname, tabItems])

   const handleTabChange = useCallback(
      (tabId: string) => {
         const tab = tabItems.find((t) => t.id === tabId)
         if (tab?.route && id && !id?.includes(":id")) {
            const alertIdToRoute = alertId || alertConfigurations[0]?.id || ""

            const routeWithAlertId = tab.route
               .replace(":id", id)
               .replace(":alertId", alertIdToRoute)
            navigate(routeWithAlertId)
         }
      },
      [alertConfigurations, alertId, id, navigate, tabItems]
   )

   const updateCellStatus = useCallback(
      async (cellId: string, statusKey: keyof LevelForm, status: boolean) => {
         try {
            await dispatch(
               updateFacilityLevel({
                  levelId: cellId,
                  levelForm: {
                     [statusKey]: status,
                  },
               })
            ).unwrap()
         } catch (error) {
            console.error(`Error setting on ${statusKey}:`, error)
         }
      },
      [dispatch]
   )

   const timezone = useSelector(selectTimezone)

   const getLogData = useCallback(
      (selectedDate: string) => {
         if (!id || !timezone) return
         try {
            dispatch(
               fetchCellLogs({
                  levelId: id,
                  timezone,
                  startDate: selectedDate,
                  endDate: selectedDate,
               })
            ).unwrap()
         } catch (err) {
            console.error("Log data fetch failed:", err)
         }
      },
      [dispatch, id, timezone]
   )

   return (
      <Grid>
         <GridItem size={10}>
            <Grid
               direction="column"
               flexWrap="nowrap"
               className="cell-detail-overview"
            >
               <Suspense fallback={<Loader />}>
                  {loadingFacilityLevel ? (
                     <Loader size={SpinnerSize.LARGE} />
                  ) : (
                     <>
                        <GridItem size={12}>
                           <Controls
                              title={name}
                              form={{ maintenance, vacant }}
                              onToggle={(status) => {
                                 updateCellStatus(
                                    id!,
                                    Object.keys(status)[0] as keyof LevelForm,
                                    Object.values(status)[0]
                                 )
                              }}
                           />
                        </GridItem>
                        <GridItem size={12}>
                           <Tabs
                              buttonsClassName="cell-detail-overview__tabs"
                              activeTabId={activeTabId}
                              tabs={tabItems}
                              onTabChange={handleTabChange}
                           />
                        </GridItem>
                        <GridItem size={12}>
                           <Outlet />
                           {cellSensor && (
                              <div className="cell-detail-overview__sensor-info">
                                 {cellSensor.serialNumber !== "" && (
                                    <span className="cell-detail-overview__sensor-info-title">
                                       Sensor ID:&nbsp;
                                       <span>{cellSensor.serialNumber}</span>
                                    </span>
                                 )}
                                 {cellSensor.macAddress !== "" && (
                                    <span className="cell-detail-overview__sensor-info-title">
                                       Mac address:&nbsp;
                                       <span>{cellSensor.macAddress}</span>
                                    </span>
                                 )}
                              </div>
                           )}
                        </GridItem>
                     </>
                  )}
               </Suspense>
            </Grid>
         </GridItem>
         <GridItem size={2}>
            {timezone && (
               <LogPanel
                  title="Cell Log"
                  timezone={timezone}
                  isExpanded={isCellLogExpanded}
                  name={name}
                  onClose={() => setIsCellLogExpanded(false)}
                  onExpand={() => setIsCellLogExpanded(true)}
                  getData={getLogData}
                  logData={cellLogs}
                  loading={loadingCellLogs}
                  error={errorCellLogs}
               />
            )}
         </GridItem>
      </Grid>
   )
}

export default CellDetailOverview
